/**
 * LiveWorkPanel — the right column of the Kiaan tab (SUN-55). Design V1 "Team cards", approved by
 * Manak on 2026-10-18 (mockups/live-work-v1). Replaces the raw "Live work" step log.
 *
 *   Team view:   Needs you (amber) → Working now (one card per agent; paused greyed) → Finished today
 *   Click views: an agent card → detail · a finished item → result · Needs you → decision
 *   Every detail view has "← Back to team".
 *
 * Words come from the server (live-work.ts): no ISO times, no "FIX 06:" titles. Times are IST.
 * Answer and Pause are shown only to people the server also allows (it refuses the rest).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  answerNeed,
  getLiveWork,
  isSlowAnswer,
  kiaanErrorText,
  pauseLiveAgent,
  type LiveWork,
  type LwAgent,
  type LwFinished,
  type LwNeed,
} from '../kiaanApi';
import { agoText, clockText, dayText } from './timeWords';

export interface LiveWorkPanelProps {
  /** Dev-mode admins answer "Needs you" questions. */
  canAnswer: boolean;
  /** Manage-agents admins may pause an agent. */
  canPause: boolean;
  onMessageAgent?: (name: string) => void;
  onOpenScreen?: (route: string) => void;
  onOpenCodeMap?: (route: string) => void;
  /** Every fresh load, so the folded right strip can show "Needs you" and who is working (SUN-56). */
  onData?: (d: LiveWork) => void;
  /** Poll interval; tests pass 0 to switch it off. */
  refreshMs?: number;
  now?: () => Date;
}

type View =
  | { kind: 'team' }
  | { kind: 'agent'; id: string }
  | { kind: 'finished'; issueId: string }
  | { kind: 'need'; issueId: string };

const FINISHED_SHOWN = 3;
const RETRY_MS = 3000;

export function LiveWorkPanel({
  canAnswer,
  canPause,
  onMessageAgent,
  onOpenScreen,
  onOpenCodeMap,
  onData,
  refreshMs = 30000,
  now = () => new Date(),
}: LiveWorkPanelProps) {
  const [data, setData] = useState<LiveWork | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>({ kind: 'team' });
  const [allFinished, setAllFinished] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const retry = useRef<number | undefined>(undefined);
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  const load = useCallback(() => {
    return getLiveWork()
      .then((d) => {
        setData(d);
        setError(null);
        onDataRef.current?.(d);
        if (d.partial) retry.current = window.setTimeout(() => void load(), RETRY_MS);
      })
      .catch((err) => {
        // A slow answer is not an outage: keep "Loading" and ask again.
        if (isSlowAnswer(err)) {
          retry.current = window.setTimeout(() => void load(), RETRY_MS);
          return;
        }
        setError(kiaanErrorText(err));
      });
  }, []);

  useEffect(() => {
    void load();
    if (!refreshMs) return undefined;
    const t = window.setInterval(() => void load(), refreshMs);
    return () => {
      window.clearInterval(t);
      window.clearTimeout(retry.current);
    };
  }, [load, refreshMs]);

  const back = () => {
    setView({ kind: 'team' });
    setNote(null);
  };

  if (!data) {
    return (
      <div className="lw" aria-label="Live work">
        <div className="kw-sec" style={{ marginTop: 0 }}>Live work</div>
        <div className="kw-small">{error ?? 'Loading live work…'}</div>
      </div>
    );
  }

  if (view.kind === 'agent') {
    const a = data.agents.find((x) => x.id === view.id);
    if (a) return <AgentView agent={a} now={now()} canPause={canPause} onBack={back} onMessage={onMessageAgent} onPaused={load} />;
  }
  if (view.kind === 'finished') {
    const f = data.finished.find((x) => x.issueId === view.issueId);
    if (f) return <FinishedView item={f} onBack={back} onOpenScreen={onOpenScreen} onOpenCodeMap={onOpenCodeMap} />;
  }
  if (view.kind === 'need') {
    const n = data.needs.find((x) => x.issueId === view.issueId);
    if (n) {
      return (
        <NeedView
          need={n}
          canAnswer={canAnswer}
          onBack={back}
          onAnswered={() => {
            // The item leaves "Needs you" now; the next load confirms it from the server.
            setData((d) => (d ? { ...d, needs: d.needs.filter((x) => x.issueId !== n.issueId) } : d));
            setView({ kind: 'team' });
            setNote('Answer sent. It is saved as a comment on that task.');
            void load();
          }}
        />
      );
    }
  }

  const working = data.agents;
  const finished = allFinished ? data.finished : data.finished.slice(0, FINISHED_SHOWN);
  const more = data.finished.length - FINISHED_SHOWN;

  return (
    <div className="lw" aria-label="Live work">
      {error && <div className="kw-small lw-warn">{error}</div>}
      {note && <div className="kw-small lw-okmsg" role="status">{note}</div>}

      {data.needs.length > 0 && (
        <div className="lw-need" data-testid="lw-needs">
          <b>Needs you ({data.needs.length})</b>
          {data.needs.map((n) => (
            <div key={n.issueId} className="lw-need-item">
              <div>{n.question}</div>
              <button type="button" className="lw-btn lw-pri" onClick={() => { setNote(null); setView({ kind: 'need', issueId: n.issueId }); }}>
                Answer
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="kw-sec" style={{ marginTop: 0 }}>Working now</div>
      {working.length === 0 && <div className="kw-small">No agents yet.</div>}
      {working.map((a) => (
        <button
          key={a.id}
          type="button"
          className={`lw-agent${a.state === 'paused' ? ' lw-paused' : ''}`}
          onClick={() => setView({ kind: 'agent', id: a.id })}
          data-testid="lw-agent"
        >
          <span className="lw-av">{a.initial}</span>
          <span className="lw-agent-body">
            <span className="lw-t"><span className={`lw-dot lw-${a.state}`} />{a.name}</span>
            <span className="lw-s">{a.sentence}</span>
            {a.progress && <Bar done={a.progress.done} total={a.progress.total} />}
            {a.state === 'working' && a.startedAt && (
              <span className="lw-s">
                {a.progress ? `${a.progress.done} of ${a.progress.total} · ` : ''}started {agoText(a.startedAt, now())}
              </span>
            )}
          </span>
        </button>
      ))}

      <div className="kw-sec">Finished today</div>
      {data.finished.length === 0 && <div className="kw-small">Nothing finished yet today.</div>}
      {finished.map((f) => (
        <button key={f.issueId} type="button" className="lw-done" onClick={() => setView({ kind: 'finished', issueId: f.issueId })} data-testid="lw-finished">
          <b>{f.area}</b> · {f.result} · {f.onLive ? 'on live' : 'on dev'}
          {f.checked ? ' · checked ✓' : ''}
        </button>
      ))}
      {more > 0 && (
        <button type="button" className="lw-more" onClick={() => setAllFinished((v) => !v)}>
          {allFinished ? 'Show fewer' : `+ ${more} more · see all`}
        </button>
      )}
    </div>
  );
}

function Bar({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <span className="lw-bar" role="progressbar" aria-valuenow={done} aria-valuemin={0} aria-valuemax={total}>
      <i style={{ width: `${pct}%` }} />
    </span>
  );
}

function Back({ onBack }: { onBack: () => void }) {
  return (
    <button type="button" className="lw-back" onClick={onBack}>
      ← Back to team
    </button>
  );
}

const STATE_WORD: Record<LwAgent['state'], string> = { working: 'Working', free: 'Free', paused: 'Paused' };

function AgentView({
  agent,
  now,
  canPause,
  onBack,
  onMessage,
  onPaused,
}: {
  agent: LwAgent;
  now: Date;
  canPause: boolean;
  onBack: () => void;
  onMessage?: (name: string) => void;
  onPaused: () => Promise<void> | void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const pause = async () => {
    setBusy(true);
    try {
      await pauseLiveAgent(agent.id);
      setMsg(`${agent.name} is paused. Resume it in Kiaan Work.`);
      await onPaused();
    } catch (err) {
      setMsg(kiaanErrorText(err));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div className="lw" aria-label={`${agent.name} details`}>
      <Back onBack={onBack} />
      <div className="lw-h">
        <span className="lw-av lw-av-lg">{agent.initial}</span>
        <div>
          <div className="lw-ttl">{agent.name}</div>
          <div className="kw-small">
            <span className={`lw-dot lw-${agent.state}`} />
            {STATE_WORD[agent.state]}
            {agent.state === 'working' && agent.startedAt ? ` · started ${agoText(agent.startedAt, now)}` : ''}
          </div>
        </div>
      </div>
      <div className="lw-card">
        <b>Right now</b>
        <div style={{ marginTop: 3 }}>{agent.sentence}</div>
        {agent.progress && (
          <>
            <Bar done={agent.progress.done} total={agent.progress.total} />
            <div className="kw-small">{agent.progress.done} of {agent.progress.total} done</div>
          </>
        )}
      </div>
      {agent.steps.length > 0 && (
        <>
          <div className="kw-sec">Steps</div>
          {agent.steps.map((s, i) => (
            <div key={i} className="lw-step" data-state={s.state}>
              <span className={`lw-ck lw-ck-${s.state}`}>{s.state === 'done' ? '✓' : s.state === 'now' ? '…' : ''}</span>
              {s.text}
            </div>
          ))}
        </>
      )}
      {agent.earlier.length > 0 && (
        <>
          <div className="kw-sec">Earlier today</div>
          <div className="lw-card kw-small">
            {agent.earlier.map((e) => (
              <div key={e.issueId}>
                {e.area} · finished{e.checked ? ' · checked ✓' : ''}
              </div>
            ))}
          </div>
        </>
      )}
      {msg && <div className="kw-small lw-okmsg" role="status">{msg}</div>}
      <div className="lw-actions">
        {onMessage && (
          <button type="button" className="lw-btn" onClick={() => onMessage(agent.name)}>
            Message {agent.name}
          </button>
        )}
        {canPause && agent.state !== 'paused' && !confirming && (
          <button type="button" className="lw-btn" onClick={() => setConfirming(true)}>
            Pause
          </button>
        )}
        {canPause && confirming && (
          <>
            <span className="kw-small">Pause {agent.name}?</span>
            <button type="button" className="lw-btn lw-pri" disabled={busy} onClick={() => void pause()}>
              Yes, pause
            </button>
            <button type="button" className="lw-btn" disabled={busy} onClick={() => setConfirming(false)}>
              No
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function FinishedView({
  item,
  onBack,
  onOpenScreen,
  onOpenCodeMap,
}: {
  item: LwFinished;
  onBack: () => void;
  onOpenScreen?: (route: string) => void;
  onOpenCodeMap?: (route: string) => void;
}) {
  // finished = the agent's report time; checked = the review's time (the server picks both)
  const checkedWhen = item.checkedAt ? ` ${dayText(item.checkedAt)} ${clockText(item.checkedAt)}` : '';
  const by = [
    item.agentName ? `by ${item.agentName}` : null,
    dayText(item.finishedAt) || null,
    clockText(item.finishedAt) || null,
    item.checked ? `checked by Claude${checkedWhen}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="lw" aria-label={`${item.area} result`}>
      <Back onBack={onBack} />
      <div className="lw-ttl">{item.area} — finished</div>
      <div className="kw-small">{by}</div>
      <div style={{ margin: '8px 0' }}>
        <span className="lw-chip">{item.onDev ? 'On dev ✓' : 'Not on dev yet'}</span>
        <span className={`lw-chip${item.onLive ? '' : ' lw-chip-a'}`}>{item.onLive ? 'On live ✓' : 'Not on live yet'}</span>
        <span className={`lw-chip${item.checked ? '' : ' lw-chip-a'}`}>{item.checked ? 'Checked ✓' : 'Waiting for the check'}</span>
      </div>
      <div className="kw-sec">What changed for your team</div>
      <div className="lw-card">
        {item.whatChanged.length ? item.whatChanged.map((l, i) => <div key={i}>• {l}</div>) : <span className="kw-small">Not written down.</span>}
      </div>
      <div className="kw-sec">How we know it works</div>
      <div className="lw-card">
        {item.howWeKnow.length ? item.howWeKnow.map((l, i) => <div key={i}>• {l}</div>) : <span className="kw-small">Not written down for this task.</span>}
      </div>
      <div className="kw-sec">Left for later</div>
      <div className="lw-card kw-small">{item.leftForLater ?? 'Nothing.'}</div>
      {item.route && (
        <div className="lw-actions">
          {onOpenScreen && (
            <button type="button" className="lw-btn" onClick={() => onOpenScreen(item.route!)}>
              Open {item.area}
            </button>
          )}
          {onOpenCodeMap && (
            <button type="button" className="lw-btn" onClick={() => onOpenCodeMap(item.route!)}>
              See on Code map
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function NeedView({
  need,
  canAnswer,
  onBack,
  onAnswered,
}: {
  need: LwNeed;
  canAnswer: boolean;
  onBack: () => void;
  onAnswered: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const send = async (body: { optionId?: string; text?: string }) => {
    setBusy(true);
    setErr(null);
    try {
      await answerNeed(need.issueId, body);
      onAnswered();
    } catch (e) {
      const status = (e as { response?: { status?: number } } | null)?.response?.status;
      setErr(status === 404 ? 'This question was already answered.' : kiaanErrorText(e));
      setBusy(false);
    }
  };

  const asked = [need.askedBy ? `Asked by ${need.askedBy}` : null, dayText(need.askedAt) || null, need.blocks ? `blocks: ${need.blocks}` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="lw" aria-label="Decision">
      <Back onBack={onBack} />
      <div className="lw-ttl">{need.question}</div>
      <div className="kw-small" style={{ margin: '4px 0 10px' }}>{asked}</div>
      {need.detail && <div className="lw-card">{need.detail}</div>}
      {need.options.map((o) => (
        <div key={o.id} className={`lw-opt${o.recommended ? ' lw-rec' : ''}`} data-testid="lw-option">
          <b>
            {o.label}
            {o.recommended ? ' (recommended)' : ''}
          </b>
          {o.detail && <div>{o.detail}</div>}
        </div>
      ))}
      {canAnswer ? (
        <div className="lw-actions">
          {need.options.map((o) => (
            <button
              key={o.id}
              type="button"
              className={`lw-btn${o.recommended ? ' lw-pri' : ''}`}
              disabled={busy}
              onClick={() => void send({ optionId: o.id })}
            >
              Choose {o.label}
            </button>
          ))}
          {need.options.length === 0 && (
            <>
              <textarea
                className="lw-text"
                aria-label="Your answer"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
              />
              <button type="button" className="lw-btn lw-pri" disabled={busy || !text.trim()} onClick={() => void send({ text: text.trim() })}>
                Send answer
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="kw-small">Only an admin can answer this.</div>
      )}
      {err && <div className="kw-small lw-warn" role="alert">{err}</div>}
      <div className="kw-small" style={{ marginTop: 10 }}>
        Your answer goes back to {need.askedBy ?? 'the team'} as a comment on that task.
      </div>
    </div>
  );
}
